import { useState } from 'react'
import { RunWithDetails } from '../../types'
import { 
  downloadFullPlanPDF, 
  downloadCompactCardPDF,
  printPDF,
  generateFullPlanPDF,
  generateCompactCardPDF
} from '../../lib/pdf-export/generators'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Download, Printer, Eye, Loader2 } from 'lucide-react'

interface ExportOptionsProps {
  run: RunWithDetails
  onPreview: (format: 'full' | 'compact') => void
}

export function ExportOptions({ run, onPreview }: ExportOptionsProps) {
  const [isGenerating, setIsGenerating] = useState({ full: false, compact: false })
  
  const handleFullPlanExport = async () => {
    setIsGenerating(prev => ({ ...prev, full: true }))
    try {
      await downloadFullPlanPDF(run)
    } catch (error) {
      console.error('Failed to export full plan PDF:', error)
      // TODO: Add toast notification for error
    } finally {
      setIsGenerating(prev => ({ ...prev, full: false }))
    }
  }

  const handleCompactCardExport = async () => {
    setIsGenerating(prev => ({ ...prev, compact: true }))
    try {
      await downloadCompactCardPDF(run)
    } catch (error) {
      console.error('Failed to export compact card PDF:', error)
      // TODO: Add toast notification for error
    } finally {
      setIsGenerating(prev => ({ ...prev, compact: false }))
    }
  }

  const handleFullPlanPrint = async () => {
    setIsGenerating(prev => ({ ...prev, full: true }))
    try {
      const blob = await generateFullPlanPDF(run)
      await printPDF(blob, `${run.title} - Full Plan`)
    } catch (error) {
      console.error('Failed to print full plan PDF:', error)
      // TODO: Add toast notification for error
    } finally {
      setIsGenerating(prev => ({ ...prev, full: false }))
    }
  }

  const handleCompactCardPrint = async () => {
    setIsGenerating(prev => ({ ...prev, compact: true }))
    try {
      const blob = await generateCompactCardPDF(run)
      await printPDF(blob, `${run.title} - Pace Card`)
    } catch (error) {
      console.error('Failed to print compact card PDF:', error)
      // TODO: Add toast notification for error
    } finally {
      setIsGenerating(prev => ({ ...prev, compact: false }))
    }
  }

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card className="bg-white border-neutral-200 shadow-minimal">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-bold text-primary-900">Full Plan PDF</CardTitle>
          <p className="text-sm text-neutral-600">
            Professional PDF with all details, nutrition breakdown, and analysis
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => onPreview('full')} 
              variant="outline" 
              size="sm"
              disabled={isGenerating.full}
            >
              <Eye className="h-4 w-4 mr-1" />
              Preview
            </Button>
            <Button 
              onClick={handleFullPlanPrint} 
              variant="outline" 
              size="sm"
              disabled={isGenerating.full}
            >
              {isGenerating.full ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Printer className="h-4 w-4 mr-1" />
              )}
              Print
            </Button>
            <Button 
              onClick={handleFullPlanExport} 
              variant="outline" 
              size="sm"
              disabled={isGenerating.full}
            >
              {isGenerating.full ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              {isGenerating.full ? 'Generating...' : 'Download PDF'}
            </Button>
          </div>
          <p className="text-xs text-neutral-500 p-3 bg-neutral-50 rounded-xl border border-neutral-200">
            <strong>Best for:</strong> Pre-race preparation and detailed analysis
          </p>
        </CardContent>
      </Card>

      <Card className="bg-white border-neutral-200 shadow-minimal">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-bold text-primary-900">Compact Pace Card PDF</CardTitle>
          <p className="text-sm text-neutral-600">
            Essential information in print-friendly format for race day
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => onPreview('compact')} 
              variant="outline" 
              size="sm"
              disabled={isGenerating.compact}
            >
              <Eye className="h-4 w-4 mr-1" />
              Preview
            </Button>
            <Button 
              onClick={handleCompactCardPrint} 
              variant="outline" 
              size="sm"
              disabled={isGenerating.compact}
            >
              {isGenerating.compact ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Printer className="h-4 w-4 mr-1" />
              )}
              Print
            </Button>
            <Button 
              onClick={handleCompactCardExport} 
              variant="outline" 
              size="sm"
              disabled={isGenerating.compact}
            >
              {isGenerating.compact ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              {isGenerating.compact ? 'Generating...' : 'Download PDF'}
            </Button>
          </div>
          <p className="text-xs text-neutral-500 p-3 bg-neutral-50 rounded-xl border border-neutral-200">
            <strong>Best for:</strong> Race day use - optimized for A4 printing and field conditions
          </p>
        </CardContent>
      </Card>
    </div>
  )
}